import { StatusCode } from "api/protocols";
import { PostNewsLetterPost, PutPublishNewsLetterPost } from "api/rpc/2024-04/facilityAdmin/facility/newsletter";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Markdown from "components/markdown/Markdown";
import Page from "components/page/Page";
import Popup from "components/popup/Popup";
import "./newsletterPosts.scss";
import TextEditor from "components/textEditor/textEditor";
import Portal from "elements/Portal";
import useModal from "hooks/modals/useModal";
import { useAppDispatch } from "hooks/redux";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";

interface IState {
    title: string;
    content: string;
}

export default function NewsletterPostNew() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const history = useHistory();
    const [state, setState] = useState<IState>({
        title: "",
        content: "",
    });
    const {state: publishModal, updateModal: updatePublishModal, closeModal: closePublishModal} = useModal();

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>){
        const {id, value} = e.target;
        setState(prev => ({...prev, [id]: value}));
    }

    async function createPost(publish?: boolean) {
        if (!state?.title) {
            dispatch(showError(t("Title field required")));
            return;
        } else if (!state?.content) {
            dispatch(showError(t("Content field required")));
            return;
        }

        dispatch(enqueue());
        
        const res = await PostNewsLetterPost({...state}, false);
        if (res?.status !== StatusCode.OK) {
            dispatch(dequeue());
            dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error creating post")));
            return;
        }
        if (publish) {
            const post = res?.data;
            const publishRes = await PutPublishNewsLetterPost({id: Number(post?.id)}, false);
            if (publishRes?.status !== StatusCode.OK) {
                dispatch(dequeue());
                dispatch(showError(typeof res?.data === "string" ? res?.data : t("Error publishing post")));
                return;
            }
            dispatch(showSuccess(t("Successfully created and published post")));
        } else {
            dispatch(showSuccess(t("Successfully created post")));
        }
        dispatch(dequeue());
        history.push("/admin/newsletter");
    }

    return (
    <Page
    title={t("New Newsletter Post")}
    breadcrumbs={[{prefix: true, label: t("Back"), url: "/admin/newsletter"}]}
    primaryAction={{content: t("Publish"), action: () => updatePublishModal({isOpen: true})}}
    secondaryActions={[{content: t("Save as Draft"), action: () => createPost()}]}
    >
     <Card>
        <Card.Section>
            <FormLayout>
                <FormLayout.Group>
                    <Input label={t("Title")} placeholder={t("Title")} onChange={handleInputChange} value={state?.title} id={"title"}/>
                </FormLayout.Group>
                <FormLayout.Group alignStretch>
                    <TextEditor label={t("Content")} markdownText={state?.content} markdownTextOnChange={value => setState(prev => ({...prev, content: value}))}/>
                <div style={{height: "100%"}}>
                  <span className="ui-email-template-preview-label">{t("Preview")}</span>
                    <div className="ui-email-template-preview-email" style={{padding: "24px"}}>
                        <div style={{width: "70%", backgroundColor: "#FFFFFF", padding: "32px", borderRadius: "12px"}}>
                        <div className="flex flex-col gap-1">
                        <p className="newsletter-posts-publish-date">{moment()?.format("ll")}</p>
                        <h1 className="newsletter-posts-header-text">{state?.title}</h1>
                        <p className="newsletter-posts-content newsletter-posts-content-expanded"><Markdown markdownText={state?.content}/></p>
                        </div>
                        </div>
                    </div>
                </div>
          
                </FormLayout.Group>
            </FormLayout>
        </Card.Section>
    </Card>   

    <Portal isMounted={publishModal?.isOpen}>
        <Popup
            open={publishModal?.isOpen}
            title={t("Publish Post")} 
            type={"info"} 
            description={t("Are you sure you want to publish this post? In addition to this post being publicly viewable by customers, the customers subscribed to the newsletter will be emailed this post")} 
            okText={t("Post")}      
            onCancel={closePublishModal}
            onOk={() => createPost(true)}
            />
    </Portal>  
      
    </Page>  
    );
}